/**
 * The **ARPG class registry** (PRD #792): the single source of truth for what
 * classes exist, which of them a player may pick, and what each one is.
 *
 * `hero.ts` reads this instead of holding a class const of its own, so adding a
 * class is content work: register a declaration and the roster, the equipment
 * allowlist, the loadout composition, and the derivation base curve all follow
 * with no new wiring. That is the storm-body and Arena class-def pattern.
 *
 * The two `playable: false` stubs that used to sit beside the Warrior are gone.
 * A class either has a full declaration or does not exist.
 */

import {
  curveAt,
  hasBalanceFloor,
  type ArpgClassDef,
  type ArpgClassId,
} from './types';
import { WARRIOR_CLASS } from './warrior';
import { ARCHER_CLASS } from './archer';
import { MAGE_CLASS } from './mage';
import { NECROMANCER_CLASS } from './necromancer';
import type { CombatBaseStats } from '../../combat/derivedStats';

export type {
  ArpgAbilitySlot,
  ArpgBalanceFloor,
  ArpgClassAbility,
  ArpgClassCurves,
  ArpgClassDef,
  ArpgClassId,
  ArpgLevelCurve,
  ArpgStartingEquipment,
  ArpgWeaponFamily,
} from './types';
export { ARPG_ABILITY_SLOTS, curveAt, hasBalanceFloor, isWornWeaponFamily } from './types';
export { WARRIOR_CLASS } from './warrior';
export { ARCHER_CLASS } from './archer';
export { MAGE_CLASS } from './mage';
export { NECROMANCER_CLASS } from './necromancer';

/** Every declared class, keyed by id, in registration order. */
export const ARPG_CLASSES: Readonly<Record<ArpgClassId, ArpgClassDef>> = {
  warrior: WARRIOR_CLASS,
  archer: ARCHER_CLASS,
  mage: MAGE_CLASS,
  necromancer: NECROMANCER_CLASS,
};

export const ARPG_CLASS_IDS: readonly ArpgClassId[] = Object.keys(
  ARPG_CLASSES,
) as ArpgClassId[];

export function isArpgClassId(id: unknown): id is ArpgClassId {
  return (
    typeof id === 'string' &&
    Object.prototype.hasOwnProperty.call(ARPG_CLASSES, id)
  );
}

/**
 * Resolve a class declaration by id.
 *
 * An unknown id returns null so the caller can refuse in fiction. It is never
 * silently defaulted: a forged or stale class id is a deliberate refusal, not
 * somebody else's Hero.
 */
export function arpgClass(id: unknown): ArpgClassDef | null {
  return isArpgClassId(id) ? ARPG_CLASSES[id] : null;
}

/**
 * Whether a player may pick this class.
 *
 * An unpopulated balance floor bars a class from the roster whatever its
 * `playable` flag says, which is how an untuned class stays away from players
 * without anybody remembering to flip a second switch.
 */
export function isPlayableArpgClassId(id: unknown): id is ArpgClassId {
  const definition = arpgClass(id);
  return definition !== null && definition.playable && hasBalanceFloor(definition.balanceFloor);
}

/**
 * The class's contribution to the derivation fold at one level.
 *
 * This is step one of the fold, and it is the only place a class curve is
 * evaluated: every consumer reads the derived record that comes out, never the
 * curve that went in.
 */
export function classBaseStats(
  definition: ArpgClassDef,
  level: number,
): CombatBaseStats {
  return {
    maxHealth: curveAt(definition.curves.maxHealth, level),
    maxMana: curveAt(definition.curves.maxMana, level),
    healthRegenPerTick: definition.curves.healthRegenPerTick,
    manaRegenPerTick: definition.curves.manaRegenPerTick,
    critChance: definition.curves.critChance,
    critMultiplier: definition.curves.critMultiplier,
    ...(definition.curves.resists ? { resists: definition.curves.resists } : {}),
  };
}
